/**
 * 真人出镜页的三条入参通道 + 「想拍什么」框的覆盖纪律(0924 车 D 二期,契约 §3.4)。
 *
 * 三条通道:
 *   ① `?templateId=<id>`  样片墙 / 外部直链带来的模板 —— 预填框 + 推荐参数;
 *   ② `?draft=<id>`       军师「当编剧」或本页「帮我写脚本」存下的草稿 —— 取回填框;
 *   ③ `location.state.explosive`  追爆仓「我来出镜拍这条」带来的零逐字套路投影
 *                         (entities/explosive `parseRealPersonHandoff` 验型)—— 自动调写脚本。
 *
 * ═══ 覆盖纪律(照抄 `briefGuidance.isReplaceableBrief` 的精神)═══════════════════════
 * 程序往框里写东西(模板种子 / AI 脚本)只在两种情况下允许覆盖:
 *   · 框是空的;
 *   · 框里**逐字等于**上一次程序写进去、用户还没动过的那份种子。
 * 用户改过一个字就不再覆盖 —— 框里是用户的,不是我们的。
 */
import { SKU, type PriceBook } from "@/entities/pricing/model/priceBook";
import { VIDEO_DEFAULT_RESOLUTION } from "@/entities/scene-card/model/creditRates";
import type { VideoResolution, VideoTemplateDto } from "../api/videoService";
import { DEFAULT_IDENTITY_REF_MODE } from "./identityRefMode";
import {
  DEFAULT_REAL_PERSON_MODEL,
  REAL_PERSON_MODEL_TIERS,
  clampDurationForModel,
  realPersonModelTier,
  realPersonQuoteMetrics,
  type RealPersonModelId,
} from "./realPersonModelTiers";

export interface RealPersonQueryIntake {
  templateId: number | null;
  draftId: string | null;
}

export function parseRealPersonQuery(search: URLSearchParams): RealPersonQueryIntake {
  const rawTpl = search.get("templateId");
  const tplNum = rawTpl != null && /^\d+$/.test(rawTpl.trim()) ? Number(rawTpl.trim()) : null;
  const rawDraft = search.get("draft");
  const draftId = rawDraft && rawDraft.trim() ? rawDraft.trim() : null;
  return { templateId: tplNum && tplNum > 0 ? tplNum : null, draftId };
}

/** 覆盖纪律单点(见头注)。`lastSeed` = 程序上一次写进框的原文(没写过为 null)。 */
export function isReplaceableRealBrief(
  current: string | null | undefined,
  lastSeed: string | null | undefined
): boolean {
  const t = (current ?? "").trim();
  if (!t) return true;
  return lastSeed != null && lastSeed.trim() === t;
}

const SLOT_RE = /\{([^{}\s]{1,12})\}/g;

/** 模板脚本里的 `{槽位}` 名(去重保序),如 `{行业}{产品}{场景}{口播}`。 */
export function templateSlots(briefScript: string | null | undefined): string[] {
  const out: string[] = [];
  for (const m of (briefScript ?? "").matchAll(SLOT_RE)) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
}

/** 框里还留着没换掉的 `{槽位}`(只提醒不拦 —— 框里是什么就发什么)。 */
export function unfilledSlots(text: string | null | undefined): string[] {
  return templateSlots(text);
}

/**
 * 卡面摘要里槽位的显示词(1004 T6)。此前一律「去掉花括号」→「{行业}公司老板」读成「行业公司老板」。
 *   ·「行业」:有租户行业就换成行业名(「珠宝首饰公司老板」),没有就删掉(「公司老板」),不编行业;
 *   · 其余槽位(产品 / 场景 / 口播)本身就是中性词,保留词面。
 */
export function slotDisplayValue(slot: string, industryName?: string | null): string {
  if (slot === "行业") return industryName?.trim() ?? "";
  return slot;
}

/** 把脚本里的「{行业}」换成租户行业名(没有行业 → 原样保留槽位,让用户自己填)。 */
export function fillIndustrySlot(
  briefScript: string | null | undefined,
  industryName?: string | null
): string {
  const name = industryName?.trim();
  const raw = briefScript ?? "";
  return name ? raw.replace(/\{行业\}/g, name) : raw;
}

/** 卡面一句话摘要:槽位换显示词(见 slotDisplayValue)、压空白、截断。 */
export function templateSummary(
  briefScript: string | null | undefined,
  max = 40,
  industryName?: string | null
): string {
  const flat = (briefScript ?? "")
    .replace(SLOT_RE, (_, slot: string) => slotDisplayValue(slot, industryName))
    .replace(/\s+/g, " ")
    .trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

function isRealPersonModel(v: string | null | undefined): v is RealPersonModelId {
  return REAL_PERSON_MODEL_TIERS.some((t) => t.value === v);
}

export interface RealPersonTemplateParams {
  model: RealPersonModelId | null;
  durationSec: number | null;
  ratio: string | null;
  /** 推荐分辨率;所选(推荐 ?? 当前)档不支持时为 null(不改当前值)。 */
  resolution: VideoResolution | null;
}

/**
 * 模板推荐参数 → 本页能表达的参数。认不出的一律 null(不改当前值,不猜):
 *   · model 只认四个真人档 id;
 *   · 时长按(推荐模型 ?? 当前模型)的上限下钳,并落到本页的时长档上(5/10/15/20/30);
 *   · 画幅只认本页六档。
 */
export function templateParams(
  t: Pick<VideoTemplateDto, "preferredModel" | "preferredDurationSec" | "preferredRatio"> &
    Partial<Pick<VideoTemplateDto, "preferredResolution">>,
  currentModel: RealPersonModelId,
  allowedRatios: readonly string[],
  allowedDurations: (model: RealPersonModelId) => readonly number[]
): RealPersonTemplateParams {
  const model = isRealPersonModel(t.preferredModel) ? t.preferredModel : null;
  const effModel = model ?? currentModel;
  let durationSec: number | null = null;
  if (typeof t.preferredDurationSec === "number" && t.preferredDurationSec > 0) {
    const clamped = clampDurationForModel(t.preferredDurationSec, effModel);
    const steps = allowedDurations(effModel);
    // 落到不超过它的最大一档(没有就取最小档)。
    durationSec = [...steps].reverse().find((s) => s <= clamped) ?? steps[0] ?? null;
  }
  const ratio =
    t.preferredRatio && allowedRatios.includes(t.preferredRatio) ? t.preferredRatio : null;
  const res = (t.preferredResolution ?? "").toLowerCase() as VideoResolution;
  const resolution = realPersonModelTier(effModel).resolutions.includes(res) ? res : null;
  return { model, durationSec, ratio, resolution };
}

/**
 * 样片卡「预估积分」:按模板推荐档(认不出落默认满血 2.0)× 默认 720p × 仅定妆照(novin,
 * 与本页缺省同口径)× 推荐时长,从价目表**单价 × 用量**现算 —— 不为每张卡各发一次报价
 * (8 张卡 = 8 个报价请求);计量口径走 `realPersonQuoteMetrics` 单源,与底栏同一把尺。
 * 查不到价 → null,卡面不显示数字(不编一个)。
 */
export function estimateTemplateCredits(
  book: PriceBook,
  t: Pick<VideoTemplateDto, "preferredModel" | "preferredDurationSec">
): number | null {
  if (!book.ready) return null;
  const model = isRealPersonModel(t.preferredModel) ? t.preferredModel : DEFAULT_REAL_PERSON_MODEL;
  const dur = clampDurationForModel(
    t.preferredDurationSec > 0 ? t.preferredDurationSec : 15,
    model
  );
  const metrics = realPersonQuoteMetrics({
    mode: "text",
    model,
    resolution: VIDEO_DEFAULT_RESOLUTION as VideoResolution,
    durationSec: dur,
    swapDurationSec: null,
    identityRefMode: DEFAULT_IDENTITY_REF_MODE,
  });
  if (!metrics) return null;
  const variant = book.variantByModel(SKU.video, metrics.modelCode, metrics.variantSuffix);
  const unit = variant ? book.unit(SKU.video, variant) : null;
  return unit == null ? null : Math.round(unit * metrics.qty);
}

/**
 * 样片是否建议带产品图(「手持产品讲解」这类带货题材)。只驱动一句提示,不强制 ——
 * 真人线锁不住产品外形,产品图参考(productRefKey,契约 §3.3)是唯一抓手。
 * 判据:模板名或脚本里出现「手持产品 / 产品讲解 / 带货 / 产品图参考」。
 */
export function templateWantsProduct(
  t: Pick<VideoTemplateDto, "name" | "briefScript"> | null | undefined
): boolean {
  if (!t) return false;
  return /手持产品|产品讲解|带货|产品图参考/.test(`${t.name ?? ""}\n${t.briefScript ?? ""}`);
}
